/**
 * LLM Prompts — Sprint 17c (updated from 16b)
 *
 * Pass 1: Source Summarizer with structured intent classification (per-source, fast tier)
 * Pass 2: Entity + Opportunity Analyzer with anti-bias instructions (cross-source, standard tier)
 */
import { INTENT_LADDER } from './pass1-schema';
// ---------------------------------------------------------------------------
// Pass 1: Structured Source Summarizer
// ---------------------------------------------------------------------------
const SOURCE_INSTRUCTIONS = {
    financial: `financial transaction data. Focus on significant purchases (high intent), recurring merchants, travel spending, dining patterns, and lifestyle indicators. A single $500 flight booking shows more intent than 100 small coffee purchases.`,
    calendar: `calendar event data. Focus on self-created events (high intent), upcoming commitments, personal vs. professional activities, social patterns. Calendar data is inherently high-intent — someone creating an event is taking action.`,
    email: `email communication data. TWO ANALYSIS AXES:
  (A) Content signals: booking confirmations and purchase receipts are high-intent (level 5), personal replies are medium-intent (level 3), newsletters are low-intent (level 1).
  (B) Relationship signals: identify PEOPLE the user communicates with personally (not automated senders). Personal correspondents with 2+ emails show a real relationship. Flag the sender name, email count, and nature of the relationship (family, friend, colleague, etc.) inferred from subjects/context.
  Both content entities AND person entities should appear in your output.`,
    browsing: `web browsing data. Focus on research sessions (deep reading, comparison shopping) as high-intent signals. Habitual social media or news visits are low-intent. Time spent and path depth indicate research intent.`,
};
/**
 * Build a Pass 1 prompt for a specific data source.
 * Sprint 17c: Requests structured JSON with per-signal intent classification.
 */
export function buildPass1Prompt(source, aggregatedData) {
    const intentLadderText = INTENT_LADDER
        .map(l => `  ${l.level} = ${l.name}: ${l.description}`)
        .join('\n');
    return `You are a personal data analyst. Your job is to classify this person's ${SOURCE_INSTRUCTIONS[source]}

INTENT COMMITMENT LADDER (classify each signal using these levels):
${intentLadderText}

RULES:
- Classify EACH meaningful signal with an intentLevel (1-5) and explain WHY
- HIGH-INTENT signals (bookings, purchases, events, research) should appear even if there's only ONE
- LOW-INTENT signals (newsletters, notifications, automated emails) should be noted but not dominate
- A single restaurant booking (intent 5) is MORE important than 50 newsletter issues (intent 1)
- Focus on what this person DOES, not what they passively receive
- Note anything time-sensitive (upcoming events, expiring subscriptions, deadlines)
- Note anything that suggests skill development, contribution to others, or career relevance
- Flag underrepresented high-intent signals that might be drowned out by volume

Return JSON with this structure (replace bracketed placeholders with real values from the data):
{
  "signals": [
    {
      "description": "[describe the actual signal from the data above]",
      "intentLevel": 5,
      "intentReason": "[why this intent level — cite the specific action]",
      "entity": "[short natural name for this interest — conversational, like 'crypto trading', 'golf', 'Italian cooking'. NOT formal taxonomy like 'Cryptocurrency Trading & Digital Assets']",
      "proxy": "initiative",
      "source": "${source}"
    }
  ],
  "underrepresentedSignals": "Optional: note any high-intent signals that might be buried under volume"
}

CRITICAL: Every signal description MUST come from the actual data above. Do NOT invent, fabricate, or reuse example data.

Valid proxy values: attention, investment, communication, repetition, initiative, priority

PRE-AGGREGATED DATA:
${aggregatedData}`;
}
// ---------------------------------------------------------------------------
// Pass 2: Entity + Opportunity Analyzer
// ---------------------------------------------------------------------------
/**
 * Format source summary for Pass 2 prompt.
 */
function formatSummarySection(source, summary, updatedAt, itemCount, isFresh) {
    if (!summary) {
        return `=== ${capitalizeSource(source)} (no data connected) ===\nNo data available.`;
    }
    const relativeTime = updatedAt ? formatRelativeTime(updatedAt) : 'unknown';
    const freshLabel = isFresh ? 'FRESH — just analyzed' : `updated ${relativeTime}`;
    const countLabel = itemCount ? `, ${itemCount} items` : '';
    return `=== ${capitalizeSource(source)} (${freshLabel}${countLabel}) ===\n${summary}`;
}
function capitalizeSource(source) {
    return source.charAt(0).toUpperCase() + source.slice(1);
}
function formatRelativeTime(timestamp) {
    const diffMs = Date.now() - timestamp;
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 60)
        return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24)
        return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}
/**
 * Format existing entities for Pass 2 prompt context.
 * Shows top entities with full detail, plus a condensed name list of all others
 * to prevent the LLM from re-creating entities it can't see.
 */
function formatExistingEntities(entities) {
    if (entities.length === 0)
        return 'No existing entities — this is the first analysis.';
    const sorted = entities
        .slice()
        .sort((a, b) => b.confidence - a.confidence);
    // Top 15 get full detail
    const top = sorted.slice(0, 15);
    const rest = sorted.slice(15);
    let result = JSON.stringify(top.map(e => ({
        name: e.name,
        type: e.type,
        confidence: +e.confidence.toFixed(2),
        trend: e.trend,
        category: e.category,
        keyEvidence: e.signals.slice(0, 2).map(s => s.summary),
    })), null, 2);
    // Show ALL remaining entity names so the LLM knows they exist and won't re-create them
    if (rest.length > 0) {
        const nameList = rest.map(e => `${e.name} (${e.type})`).join(', ');
        result += `\n\nADDITIONAL EXISTING ENTITIES (reuse these names if you detect the same interest — do NOT create duplicates):\n${nameList}`;
    }
    return result;
}
/**
 * Build the Pass 2 prompt that analyzes all sources together.
 * Sprint 17c: Enhanced with chain-of-thought, anti-bias instructions, few-shot examples.
 */
export function buildPass2Prompt(input) {
    const summaryBlocks = input.summaries
        .map(s => formatSummarySection(s.source, s.summary, s.updatedAt, s.itemCount, s.isFresh))
        .join('\n\n');
    const existingEntitiesBlock = formatExistingEntities(input.existingEntities);
    const feedbackBlock = input.feedbackSummary
        ? `\nUSER FEEDBACK HISTORY:\n${input.feedbackSummary}\nUse this feedback to adjust your analysis. Entities the user repeatedly dismisses should get lower confidence. Entities they accept or complete should be reinforced.\n`
        : '';
    const profileBlock = input.profileContext
        ? `\nUSER PROFILE CONTEXT:\n${input.profileContext}\nUse this profile to personalize entity detection. Respect the DO NOT SUGGEST list. Weight opportunities toward the user's top interests and active contexts.\n`
        : '';
    const relationshipBlock = input.emailRelationships
        ? `\nRELATIONSHIP SIGNALS (from email):\nThese are the people this user communicates with personally. Use these to create "person" type entities.\n${input.emailRelationships}\n`
        : '';
    return `You are analyzing a person's behavioral data to understand what they love and identify actionable opportunities.

TODAY'S DATE: ${input.currentDate}

SOURCE SUMMARIES:
${summaryBlocks}
${relationshipBlock}
EXISTING ENTITIES (from prior analysis):
${existingEntitiesBlock}
${feedbackBlock}${profileBlock}
AVAILABLE AGENT CAPABILITIES:
You can suggest actions that use these services:
- Restaurant search (find restaurants by cuisine, location, price, occasion)
- Travel search (find flights, hotels, destinations by budget and dates)
- Content discovery (find articles, videos, courses by topic)
- Shopping search (find products by category, price range, brand)
- Event search (find local events, meetups, classes by interest)
- Financial analysis (spending insights, savings opportunities, budget optimization)

REASONING PROCESS (think through these steps internally, but DO NOT include them in your response):
1. List the top 3 highest-intent signals from EACH source.
2. Identify cross-source convergence (signals across 2+ sources).
3. Volume bias check — downweight entities based only on high volume (newsletters, notifications).
4. Source coverage — ensure every connected source contributes at least one entity.
5. Generate entities and opportunities.

YOUR RESPONSE MUST CONTAIN ONLY the JSON object below — no explanation, no chain-of-thought, no markdown.

DO NOT:
- Create entities primarily because a sender has many emails. Volume ≠ interest.
- Generate more than 2 entities from the same category unless evidence is overwhelming.
- Create near-duplicate entities with different word orderings of the same concept.
  BAD: "Robotic Lawn Mowing & Garden Automation" AND "Robotic Lawn Automation & Garden Technology"
  BAD: "Fine Dining & Restaurant Reservations" AND "Premium Dining & Fine Restaurant Experiences"
  GOOD: Pick ONE name for each distinct interest.
- Ignore low-volume, high-intent signals (bookings, purchases, events) in favor of
  high-volume, low-intent signals (newsletters, notifications).
- Create generic opportunities like "Check out deals" or "Explore options" —
  every opportunity must reference specific evidence from the data.

FEW-SHOT EXAMPLE — Volume Bias vs Intent-Based Analysis:
Given: 47 newsletters from a single sender + 1 completed restaurant booking
BAD: Creating an entity from the newsletter topic at 0.9 confidence — this is VOLUME BIAS.
     Passive newsletter subscriptions do not indicate the user wants missions about that topic.
GOOD: Creating a dining entity at 0.7 confidence from 1 booking + 2 restaurant transactions —
      this is INTENT-BASED. A completed booking (intent level 5) outweighs passive newsletters (intent level 1).

CRITICAL: The above is a PATTERN to follow, not data to copy. All entity names, signals, and evidence MUST come from the actual source data provided — never from these instructions.

INSTRUCTIONS:
1. Identify the things, experiences, AND people this person loves.
   ALL THREE entity types must be represented if evidence exists:
   - "thing": products, hobbies, topics (from purchases, browsing, subjects)
   - "experience": activities, events, rituals (from calendar, bookings, outings)
   - "person": specific people the user has personal relationships with
     (from email correspondents, calendar attendees, shared activities).
     Use the actual person's name as the entity name (e.g., "Sarah Thompson").
   Cross-reference across sources — convergence = higher confidence.
2. For each entity, cite evidence using the 6 behavioral proxies:
   Attention (time spent), Investment (money spent), Communication (writes/talks about),
   Repetition (does again), Initiative (seeks out), Priority (chooses when constrained)
3. Set confidence 0-1 based on evidence strength and source convergence.
4. Assess ikigaiRelevance — how does this entity relate to the four Ikigai dimensions?
   - love (0-1): How much joy/passion does this bring? Evidence of emotional investment?
   - goodAt (0-1): Does the data show skill, competence, or improvement?
   - worldNeeds (0-1): Does this serve others, contribute to community, or address a need?
   - paidFor (0-1): Is there income, career, or economic value connected to this?
   Entities scoring high across multiple dimensions represent the user's Ikigai —
   the intersection where personal fulfillment and practical contribution meet.
5. Set stability (0-1) for how enduring this entity is:
   0.0-0.2 = novelty/impulse, 0.3-0.5 = recurring interest, 0.6-0.8 = established pattern,
   0.9-1.0 = core identity (years of evidence, defines who this person is).
6. Set nature — a short phrase describing this entity's role in the user's life
   (e.g., "lifelong passion", "growing interest", "daily ritual", "aspirational goal",
   "professional pursuit", "social anchor").
ENTITY NAME FORMAT:
- Short, natural, conversational — how a real person describes their interest.
- Lowercase unless proper noun (e.g., "crypto trading", "golf", "Japanese food").
- NEVER use formal taxonomy names with ampersands and sub-categories.
  BAD: "Cryptocurrency Trading & Digital Assets", "Golf & Course Management"
  GOOD: "crypto trading", "golf"
- If an existing entity has a formal taxonomy-style name, output the natural version instead.
  The system will match them via fuzzy matching.
7. Populate actionableContext with free-form descriptions (not categories):
   - location, budget, socialContext, timing, frequency, constraints
   - Use natural language: "$30-50 per person" not "moderate"
   - Include 1-3 suggestedActions the available agents could execute.
8. For time-sensitive entities: set deadline (ISO 8601) and urgencyReason.
   Set temporalPattern for recurring/seasonal patterns (e.g., "peaks every December").
9. CRITICAL — Entity deduplication and continuity:
   - If an existing entity matches what you detect, reuse its name UNLESS it uses
     formal taxonomy style (e.g., "Cryptocurrency Trading & Digital Assets"). In that case,
     output the natural conversational version ("crypto trading"). The system handles
     name migration via fuzzy matching.
   - Never create two entities about the same topic. Merge related signals into ONE entity.
   - Each entity should cover a SPECIFIC interest, not a broad category.
     BAD: "Technology & Product Development" — too vague.
     GOOD: "OwnYou Privacy App Development" — specific and grounded in the data.
   - Aim for completeness — detect ALL entities supported by evidence. Mark each with
     confidence (0-1). It's better to detect a low-confidence entity (0.3) than to miss it
     entirely. The downstream system handles filtering.
10. CRITICAL — Opportunity completeness: For EACH entity, consider ALL 6 agent capabilities
    and generate an opportunity for every one that makes sense:
    - SHOPPING: Would this person benefit from buying products related to this interest?
    - RESTAURANT: Is there a dining experience connected to this entity?
    - TRAVEL: Could this interest inspire a trip, day out, or destination visit?
    - EVENTS: Are there classes, meetups, concerts, exhibitions, or workshops they'd enjoy?
    - CONTENT: Would articles, tutorials, podcasts, videos, or courses deepen this interest?
    - FINANCIAL: Are there spending insights, savings opportunities, or budget optimizations?

    Generate an opportunity for EACH applicable type. A single entity like "Photography"
    should produce: shopping (camera gear), events (photo walks), content (tutorials),
    travel (scenic destinations), and possibly restaurant (food photography spots).

    It is better to over-generate (downstream quality gates filter) than to miss a relevant
    opportunity. Every opportunity must have a specific, actionable description grounded in
    the user's actual data — not generic text like "Check out deals".

Return ONLY this JSON structure (no other text, no markdown fences). Replace ALL bracketed placeholders with real values from the data — never copy example text:
{
  "entities": [
    {
      "name": "[short natural name — conversational, not taxonomy-style]",
      "type": "thing|experience|person",
      "nature": "[short phrase: e.g. growing interest, daily ritual, professional pursuit]",
      "category": "[category derived from the data]",
      "subcategories": ["[subcategory1]", "[subcategory2]"],
      "confidence": 0.85,
      "stability": 0.8,
      "reasoning": "[cite specific cross-source evidence from the data above]",
      "ikigaiRelevance": {
        "love": 0.9,
        "goodAt": 0.6,
        "worldNeeds": 0.1,
        "paidFor": 0.0
      },
      "proxies": {
        "attention": "[describe actual attention evidence from the data]",
        "investment": "[describe actual spending evidence from the data]"
      },
      "signals": [
        {"source": "financial", "proxy": "investment", "summary": "[actual financial evidence]", "strength": 0.8},
        {"source": "calendar", "proxy": "attention", "summary": "[actual calendar evidence]", "strength": 0.9}
      ],
      "actionableContext": {
        "location": "[infer from the data, or omit if unknown]",
        "budget": "[infer from spending patterns in the data]",
        "socialContext": "[infer from the data, or omit if unknown]",
        "timing": "[infer from calendar/behavioral patterns]",
        "frequency": "[infer from repetition in the data]",
        "constraints": "[infer from the data, or omit if unknown]",
        "suggestedActions": [
          "[specific action an agent could take based on the evidence]",
          "[another specific action based on the evidence]"
        ]
      },
      "temporalPattern": "[describe pattern if visible in the data, or omit]",
      "status": "new|confirmed|updated|merged|declining"
    }
  ],
  "opportunities": [
    {
      "entityName": "[name of an entity from above]",
      "description": "[time-sensitive opportunity found in the data]",
      "deadline": "[ISO 8601 date if applicable]",
      "urgencyReason": "[why this is time-sensitive]",
      "suggestedMission": "[specific actionable mission]",
      "agentType": "shopping|restaurant|travel|content|events|financial",
      "context": {"budget": "[from the data]", "location": "[from the data]"}
    }
  ]

Agent type mapping guide (use the BEST match for each entity):
- "shopping" → product purchases, deals, items to buy (e.g. "Clothing and Accessories", "Home Improvement")
- "restaurant" → dining, food, restaurant bookings (e.g. "Dining Out", "Sushi")
- "travel" → flights, hotels, trip planning (e.g. "Travel Bookings")
- "content" → articles, news, podcasts, reading lists (e.g. "Cryptocurrency Research", "Productivity Tools")
- "events" → concerts, meetups, classes, shows, exhibitions, sports (e.g. "Live Music", "Community Engagement", "Photography Workshops")
- "financial" → budgeting, savings goals, investment tracking (e.g. "Subscription Management")

IMPORTANT: "events" means live experiences with a date/venue — use it for hobbies, entertainment, sports, community activities. Do NOT assign "events" to financial/market topics.
}

CRITICAL: Every entity name, signal summary, evidence description, and opportunity MUST be derived from the actual source data above. Do NOT copy or paraphrase any text from these instructions. If you cannot find evidence in the data for an entity, do not create it.`;
}
/**
 * Build a retry prompt when Pass 2 fails validation.
 */
export function buildPass2RetryPrompt(originalPrompt, errorMessage) {
    return `${originalPrompt}

IMPORTANT: Your previous response failed validation with this error:
${errorMessage}

Please fix the JSON format and try again. Ensure all required fields are present and values are within the specified ranges.`;
}
//# sourceMappingURL=prompts.js.map